/**
 * @contract
 *   Назначение: Единый хедер публичного портала OXPA.PRO
 *   Маршрут:   Все страницы (public) route group
 *   Автор:     AI Agent (Редизайн)
 *   Дата:      2026-03-23
 */
"use client";

import Link from "next/link";
import { usePathname } from "next/navigation";
import { Menu, X } from "lucide-react";
import { Button } from "@oxpa/shared/ui/button";
import { useState } from "react";

const NAV_ITEMS = [
  { href: "/rating", label: "Рейтинг" },
  { href: "/wiki", label: "База знаний" },
  { href: "/calc", label: "Калькулятор" },
  { href: "/blog", label: "Блог" },
  { href: "/guard-portal", label: "Паспорт охранника" },
];

export function PublicHeader() {
  const pathname = usePathname();
  const [mobileOpen, setMobileOpen] = useState(false);

  return (
    <header className="sticky top-0 z-50 w-full border-b border-[hsl(var(--portal-border))] portal-header-glass">
      <div className="container mx-auto flex h-[68px] items-center justify-between px-4 md:px-6">
        {/* Logo */}
        <Link
          href="/"
          className="flex items-center gap-3 hover:opacity-80 transition-opacity"
          aria-label="OXPA.PRO — на главную"
        >
          <span
            className="h-7 w-7 rounded-full flex-none bg-[hsl(var(--portal-accent))]"
            aria-hidden="true"
          />
          <span className="font-bold tracking-[0.04em] font-mono text-lg text-[hsl(var(--portal-text))]">
            OXPA.PRO
          </span>
        </Link>

        {/* Desktop Nav */}
        <nav className="hidden md:flex items-center gap-1 ml-auto" aria-label="Главная навигация">
          {NAV_ITEMS.map((item) => {
            const isActive = pathname === item.href || pathname.startsWith(item.href + "/");
            return (
              <Link
                key={item.href}
                href={item.href}
                className={`px-[11px] py-[10px] text-[13px] uppercase tracking-[0.05em] font-medium font-mono border transition-colors ${
                  isActive
                    ? "text-[hsl(var(--portal-text))] border-[hsl(var(--portal-border))]"
                    : "text-[hsl(var(--portal-text-secondary))] border-transparent hover:text-[hsl(var(--portal-text))] hover:border-[hsl(var(--portal-border))]"
                }`}
              >
                {item.label}
              </Link>
            );
          })}
        </nav>

        {/* CTA + Mobile Toggle */}
        <div className="flex items-center gap-3">
          <Link href="/login" className="hidden md:block">
            <span className="text-[13px] uppercase tracking-[0.06em] font-medium font-mono text-[hsl(var(--portal-text-secondary))] hover:text-[hsl(var(--portal-text))] transition-colors">
              Войти
            </span>
          </Link>
          <Link href="/register" className="hidden md:block">
            <Button
              size="sm"
              className="bg-[hsl(var(--portal-accent))] text-black hover:bg-[hsl(var(--portal-accent-hover))] rounded-none uppercase tracking-[0.06em] text-[13px] font-bold px-5 py-[15px] leading-none border-2 border-[hsl(var(--portal-accent))] portal-btn-brutal font-mono"
            >
              Начать
            </Button>
          </Link>
          <button
            className="md:hidden w-[42px] h-[42px] border-2 border-[hsl(var(--portal-border))] bg-transparent text-[hsl(var(--portal-text))] cursor-pointer flex items-center justify-center"
            onClick={() => setMobileOpen(!mobileOpen)}
            aria-label="Меню"
            aria-expanded={mobileOpen}
          >
            {mobileOpen ? <X className="w-5 h-5" /> : <Menu className="w-5 h-5" />}
          </button>
        </div>
      </div>

      {/* Mobile Menu */}
      {mobileOpen && (
        <nav className="md:hidden fixed inset-[68px_0_0_0] z-40 bg-[hsl(var(--portal-bg))] px-6 py-8 border-t border-[hsl(var(--portal-border))]" aria-label="Мобильное меню">
          {NAV_ITEMS.map((item) => (
            <Link
              key={item.href}
              href={item.href}
              onClick={() => setMobileOpen(false)}
              className="block text-lg uppercase tracking-[0.04em] font-semibold font-mono py-[18px] border-b border-[hsl(var(--portal-border))] text-[hsl(var(--portal-text))] hover:text-[hsl(var(--portal-accent))] transition-colors"
            >
              {item.label}
            </Link>
          ))}
          <div className="pt-4 space-y-2">
            <Link href="/register" onClick={() => setMobileOpen(false)}>
              <span className="block text-center py-3 text-lg uppercase tracking-[0.04em] font-bold font-mono bg-[hsl(var(--portal-accent))] text-black">
                Начать
              </span>
            </Link>
            <Link href="/login" onClick={() => setMobileOpen(false)}>
              <span className="block text-center py-3 text-lg uppercase tracking-[0.04em] font-semibold font-mono text-[hsl(var(--portal-text-secondary))]">
                Войти
              </span>
            </Link>
          </div>
        </nav>
      )}
    </header>
  );
}
